import { type CSSProperties } from 'react';

import { Container } from '@/components/ui/container';
import { trustStrip } from '@/content/site';

/** Base logo height (px) in the marquee; each logo's per-logo `scale` multiplies it. */
const LOGO_H = 26;

/**
 * A customer logo rendered as a single-color silhouette via CSS mask, so logos
 * of any source format/color (SVG or white PNG) all take one uniform color —
 * the current text color (`bg-current`). Edit that color in one place on the
 * marquee container below.
 *
 * Height = LOGO_H × `scale`, width = height × `ratio` (aspect preserved), so
 * `scale` grows/shrinks a single logo to optically match the others. `scale`
 * defaults to 1.
 */
function Logo({
  name,
  src,
  ratio,
  scale = 1,
}: {
  name: string;
  src: string;
  ratio: number;
  scale?: number;
}) {
  const height = LOGO_H * scale;
  return (
    <li className="shrink-0">
      <span
        role="img"
        aria-label={name}
        className="block bg-slate-500"
        style={
          {
            height,
            width: height * ratio,
            WebkitMaskImage: `url(${src})`,
            maskImage: `url(${src})`,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
          } as CSSProperties
        }
      />
    </li>
  );
}

export function TrustStrip() {
  // Duplicated so the marquee can loop seamlessly.
  const logos = [...trustStrip.logos, ...trustStrip.logos];

  return (
    <section className="border-y border-hairline bg-slate-50 py-8">
      {/* Stacked on mobile (title above the marquee), side-by-side on sm+. */}
      <Container className="flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-8">
        <div className="text-center text-sm font-base text-slate-500 sm:w-[195px] sm:flex-shrink-0 sm:text-left">
          {trustStrip.title}
        </div>

        {/* Marquee. `text-ink-400` here sets the single color for every logo
            (each Logo uses bg-current); change it to recolor them all. */}
        <div className="mask-fade-x w-full overflow-hidden text-ink-400 sm:w-auto sm:min-w-0 sm:flex-1">
          <ul className="flex w-max animate-marquee items-center gap-16">
            {logos.map((logo, i) => (
              <Logo key={`${logo.name}-${i}`} {...logo} />
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}
